import { addBehaviorEvents } from "../api/analytics"

const DEVICE_KEY = "starlive_analytics_device"
const SESSION_KEY = "starlive_analytics_session"
const FLUSH_INTERVAL = 2000

function randomId(prefix) {
  if (window.crypto && typeof window.crypto.randomUUID === "function") return `${prefix}:${window.crypto.randomUUID()}`
  return `${prefix}:${Date.now().toString(36)}:${Math.random().toString(36).slice(2)}`
}

function storedId(storage, key, prefix) {
  let value = storage.getItem(key)
  if (!value) {
    value = randomId(prefix)
    storage.setItem(key, value)
  }
  return value
}

function stableName(value, fallback = "unknown") {
  const name = String(value || "").trim().replace(/[^a-zA-Z0-9._:-]/g, "_")
  return name.slice(0, 100) || fallback
}

class BehaviorAnalytics {
  constructor() {
    this.deviceId = storedId(localStorage, DEVICE_KEY, "device")
    this.sessionId = storedId(sessionStorage, SESSION_KEY, "session")
    this.current = null
    this.suspendedView = null
    this.queue = []
    this.flushTimer = 0
    this.started = false
    this.clickHandler = this.captureClick.bind(this)
    this.visibilityHandler = this.handleVisibility.bind(this)
    this.pageHideHandler = this.handlePageHide.bind(this)
  }

  start(page = "home", tab = "recommend") {
    if (!this.started) {
      this.started = true
      document.addEventListener("click", this.clickHandler, true)
      document.addEventListener("visibilitychange", this.visibilityHandler)
      window.addEventListener("pagehide", this.pageHideHandler)
    }
    this.enter(page, tab, false)
  }

  enter(page, tab = "", pathTransition = true) {
    const target = { page: stableName(page), tab: stableName(tab, "default"), enteredAt: Date.now() }
    if (this.current && this.current.page === target.page && this.current.tab === target.tab) return
    const source = this.current ? { ...this.current } : null
    if (source) this.leaveCurrent("navigation")
    if (pathTransition && source) {
      this.push("view_transition", {
        page_name: target.page,
        tab_name: target.tab,
        source_page: source.page,
        source_tab: source.tab,
        target_page: target.page,
        target_tab: target.tab
      })
    }
    this.current = target
    this.push("view_enter", { page_name: target.page, tab_name: target.tab })
  }

  trackClick(elementName, elementType = "button") {
    if (!this.current) return
    this.push("element_click", {
      page_name: this.current.page,
      tab_name: this.current.tab,
      element_name: stableName(elementName),
      element_type: stableName(elementType)
    })
  }

  captureClick(event) {
    const target = event.target && event.target.closest
      ? event.target.closest("[data-analytics],button,a,[role='button']")
      : null
    if (!target || target.disabled || target.closest("[data-analytics-ignore]")) return
    const explicit = target.getAttribute("data-analytics")
    // 只采集显式稳定锚点。aria-label 会随语言变化，也可能包含昵称等动态内容，禁止作为锚点回退值。
    if (!explicit) return
    this.trackClick(explicit, target.tagName ? target.tagName.toLowerCase() : "control")
  }

  leaveCurrent(reason) {
    if (!this.current) return
    const duration = Math.max(0, Date.now() - this.current.enteredAt)
    this.push("view_leave", {
      page_name: this.current.page,
      tab_name: this.current.tab,
      element_name: stableName(reason, "leave"),
      duration_ms: duration
    })
    this.current = null
  }

  handleVisibility() {
    if (document.visibilityState === "hidden") {
      this.suspendedView = this.current ? { page: this.current.page, tab: this.current.tab } : null
      this.leaveCurrent("hidden")
      this.flush()
      return
    }
    if (!this.current && this.suspendedView) {
      this.enter(this.suspendedView.page, this.suspendedView.tab, false)
      this.suspendedView = null
    }
  }

  handlePageHide() {
    this.leaveCurrent("pagehide")
    this.flush()
  }

  push(eventName, payload) {
    this.queue.push({
      event_id: randomId("event"),
      device_id: this.deviceId,
      session_id: this.sessionId,
      event_name: eventName,
      ...payload
    })
    if (this.queue.length >= 20) this.flush()
    else if (!this.flushTimer) this.flushTimer = window.setTimeout(() => this.flush(), FLUSH_INTERVAL)
  }

  async flush() {
    window.clearTimeout(this.flushTimer)
    this.flushTimer = 0
    if (!this.queue.length) return
    const batch = this.queue.splice(0, 50)
    try {
      await addBehaviorEvents(batch)
    } catch (error) {
      // 埋点失败不得阻塞用户操作；保留最近事件供下一次可见交互重试。
      this.queue = batch.concat(this.queue).slice(-100)
    }
  }

  destroy() {
    this.leaveCurrent("destroy")
    this.flush()
    document.removeEventListener("click", this.clickHandler, true)
    document.removeEventListener("visibilitychange", this.visibilityHandler)
    window.removeEventListener("pagehide", this.pageHideHandler)
    this.started = false
  }
}

export default new BehaviorAnalytics()
